import { useState } from "react";
import { Link } from "react-router-dom";
import { X, FileText } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { LEGAL_VERSION } from "../lib/legal";
import { api } from "../lib/api";

/**
 * One-time banner that appears when the current user hasn't acknowledged the
 * latest version of Terms + Privacy. Tapping "Got it" PATCHes their user doc
 * so the banner stays dismissed across devices; bumping LEGAL_VERSION makes
 * it reappear for everyone — clean UK GDPR notification trail.
 *
 * Hidden until the auth context has loaded a user. Also hides on /terms and
 * /privacy themselves so it doesn't compete with the page they're reading.
 */
export default function LegalConsentBanner() {
  const { user, refresh } = useAuth();
  const [acking, setAcking] = useState(false);
  const [localDismissed, setLocalDismissed] = useState(false);

  if (!user) return null;
  if (user.legal_acknowledged_version === LEGAL_VERSION) return null;
  if (localDismissed) return null;
  // Suppress on the legal pages themselves.
  if (typeof window !== "undefined") {
    const p = window.location.pathname;
    if (p.startsWith("/terms") || p.startsWith("/privacy")) return null;
  }

  const acknowledge = async () => {
    setAcking(true);
    // Optimistic dismiss so the user doesn't see a stuck spinner.
    setLocalDismissed(true);
    try {
      await api.post("/auth/me/acknowledge-legal", { version: LEGAL_VERSION });
      if (refresh) await refresh();
    } catch {
      // If the call fails (offline) we still keep it dismissed for the
      // current session — the next online launch will re-prompt.
    } finally {
      setAcking(false);
    }
  };

  return (
    <div
      data-testid="legal-consent-banner"
      className="fixed inset-x-0 bottom-20 sm:bottom-4 z-40 px-4 pointer-events-none"
    >
      <div
        className="mx-auto max-w-md rounded-2xl shadow-2xl pointer-events-auto"
        style={{
          background: "var(--primary)",
          color: "#F2EEE7",
          border: "1.5px solid rgba(255,255,255,0.12)",
        }}
      >
        <div className="flex items-start gap-3 p-4">
          <div
            className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(255,255,255,0.15)" }}
          >
            <FileText size={18} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-sm leading-tight">
              We&apos;ve updated our Terms &amp; Privacy
            </div>
            <p className="text-[12px] opacity-80 mt-0.5 leading-snug">
              A quick read covering how we handle your data, GPS tracking and Pro billing.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={acknowledge}
                disabled={acking}
                data-testid="legal-consent-got-it"
                className="text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full transition-colors"
                style={{ background: "var(--accent-terracotta)", color: "#F2EEE7" }}
              >
                {acking ? "Saving…" : "Got it"}
              </button>
              <Link
                to="/terms"
                data-testid="legal-consent-read-terms"
                className="text-[11px] uppercase tracking-wider font-bold underline opacity-90 hover:opacity-100"
              >
                Read terms
              </Link>
              <Link
                to="/privacy"
                data-testid="legal-consent-read-privacy"
                className="text-[11px] uppercase tracking-wider font-bold underline opacity-90 hover:opacity-100"
              >
                Privacy
              </Link>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            data-testid="legal-consent-close"
            onClick={() => setLocalDismissed(true)}
            className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 hover:bg-white/10 transition-colors"
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
